import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  usePayPalScriptReducer,
  PayPalButtons,
  PayPalCardFieldsProvider,
  PayPalNameField,
  PayPalNumberField,
  PayPalExpiryField,
  PayPalCVVField,
  usePayPalCardFields,
} from "@paypal/react-paypal-js";
import {
  CompletePaymentMutation,
  CreateProviderPaymentMutation,
} from "arena/mutations";
import { useMutation } from "@apollo/client";
import { withSnackbar } from "notistack";
import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid";
import ReactGA from "react-ga4";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";

function PaypalCheckout({
  paymentId,
  enqueueSnackbar,
  setError,
  setLoading,
  paymentData,
  loading,
  descriptionComponent,
  paymentHasError,
  setPaymentHasError,
}) {
  const theme = useTheme();
  const style = {
    layout: "vertical",
    color: theme.palette.mode === "dark" ? "black" : "white",
    label: "pay",
  };

  const { t } = useTranslation("arena");

  const cardFieldsStyle = {
    input: {
      fontSize: theme.typography.body1.fontSize,
      fontFamily:
        "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      color: theme.palette.text.primary,
      lineHeight: theme.typography.body1.lineHeight,
      padding: `${theme.spacing(0.5)}px ${theme.spacing(0.75)}px`,
      height: `${theme.spacing(4)}px`,
      borderRadius: theme.shape.borderRadius,
      background: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
    },
    ":focus": {
      borderColor: theme.palette.primary.main,
      outline: "none",
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: "none",
    },
    ".invalid": {
      color: theme.palette.error.main,
      boxShadow: "none",
    },
    ".valid": {
      color: theme.palette.text.primary,
    },
    ":focus.invalid": {
      boxShadow: "none",
    },
  };

  const [{ isPending, isResolved }] = usePayPalScriptReducer();
  const [paypalError, setPaypalError] = useState(false);
  const [paypalErrorDescription, setPaypalErrorDescription] = useState();
  const [cardFieldsEligible, setCardFieldsEligible] = useState(null);
  const cardFieldsReadyRef = useRef(false);
  const cardFieldsTimeoutRef = useRef(null);

  useEffect(() => {
    setLoading(isPending);
  }, [isPending]);

  useEffect(() => {
    if (isResolved) {
      console.log("PayPal SDK loaded successfully");
    }
  }, [isResolved]);

  useEffect(() => {
    cardFieldsReadyRef.current = false;

    if (cardFieldsTimeoutRef.current) {
      clearTimeout(cardFieldsTimeoutRef.current);
      cardFieldsTimeoutRef.current = null;
    }

    if (!isResolved || !paymentData.orderData) {
      setCardFieldsEligible(null);
      return;
    }

    setCardFieldsEligible(null);

    cardFieldsTimeoutRef.current = setTimeout(() => {
      if (!cardFieldsReadyRef.current) {
        console.warn(
          "PayPal Card Fields are not available or not eligible for this configuration"
        );
        setCardFieldsEligible(false);
      }
    }, 2500);

    return () => {
      if (cardFieldsTimeoutRef.current) {
        clearTimeout(cardFieldsTimeoutRef.current);
        cardFieldsTimeoutRef.current = null;
      }
    };
  }, [isResolved, paymentData.orderData]);

  const prepareOrderCreation = () => {
    setPaypalError(false);
    setPaypalErrorDescription(false);

    ReactGA.event({
      category: "Product",
      action: "Start pay",
      label: "paypal",
    });
  };

  const ensureOrderData = () => {
    if (!paymentData?.orderData) {
      const error = new Error("PayPal order data is not available");
      console.error(error);
      setPaypalError(true);
      setPaypalErrorDescription(
        t(
          "paypalPaymentForm.orderDataUnavailable",
          "Данные заказа недоступны. Попробуйте обновить страницу."
        )
      );
      throw error;
    }

    return paymentData.orderData;
  };

  const [completePayment] = useMutation(CompletePaymentMutation, {
    onError(error) {
      setLoading(false);
      setError(error);
      enqueueSnackbar(error.message, {
        variant: "error",
      });
    },
  });

  const [createProviderPayment] = useMutation(CreateProviderPaymentMutation, {
    onError(error) {
      setLoading(false);
      setPaypalError(true);
      setPaypalErrorDescription(
        error.message ||
          t(
            "paypalPaymentForm.orderCreationError",
            "Не удалось создать заказ. Попробуйте еще раз."
          )
      );
      enqueueSnackbar(
        error.message ||
          t(
            "paypalPaymentForm.orderCreationError",
            "Не удалось создать заказ. Попробуйте еще раз."
          ),
        {
          variant: "error",
        }
      );
    },
  });

  const createOrder = (data, actions) => {
    prepareOrderCreation();

    const orderData = ensureOrderData();

    if (!actions?.order?.create) {
      const error = new Error("PayPal actions.order.create is not available");
      console.error(error, actions);
      setPaypalError(true);
      setPaypalErrorDescription(
        t(
          "paypalPaymentForm.orderCreationUnavailable",
          "Не удалось инициализировать оплату. Попробуйте обновить страницу."
        )
      );
      return Promise.reject(error);
    }

    return actions.order.create(orderData);
  };

  const createOrderForCardFields = async () => {
    prepareOrderCreation();

    try {
      const result = await createProviderPayment({
        variables: {
          id: paymentId,
        },
      });

      const providerData = result.data.createProviderPayment;

      if (!providerData || !providerData.id) {
        throw new Error("Invalid response from createProviderPayment");
      }

      // Для Card Fields нужно вернуть order ID (строку)
      return providerData.id;
    } catch (error) {
      console.error("Error creating provider payment:", error);
      setPaypalError(true);
      setPaypalErrorDescription(
        error.message ||
          t(
            "paypalPaymentForm.orderCreationError",
            "Не удалось создать заказ. Попробуйте еще раз."
          )
      );
      throw error;
    }
  };

  const createSubscription = (data, actions) => {
    setPaypalError(false);

    ReactGA.event({
      category: "Product",
      action: "Start pay",
      label: "paypal",
    });

    return actions.subscription.create(paymentData.subscriptionData);
  };

  const emitSuccessEvent = () => {
    if (paymentHasError) {
      ReactGA.event({
        category: "Product",
        action: "Success pay after error",
        label: "paypal",
      });
    } else {
      ReactGA.event({
        category: "Product",
        action: "Success pay",
        label: "paypal",
      });
    }
  };

  const onApprove = (data, actions) => {
    setLoading(true);
    setPaypalError(false);
    setPaypalErrorDescription(false);

    ReactGA.event({
      category: "Product",
      action: "Start complete payment",
      label: "paypal",
    });

    completePayment({
      variables: {
        id: paymentId,
        input: { paypalData: data },
      },
    })
      .then((data) => {
        const payment = data.data.completePayment;

        /**
         * Платеж может прилететь с задержкой
         */
        if (payment.status === "PENDING") {
          if (!payment.completeError) {
            emitSuccessEvent();

            enqueueSnackbar(t("paypalPaymentForm.paymentCompleted"), {
              variant: "success",
            });

            window.location = payment.returnUrl;

            return;
          }
        }

        if (payment.status === "SUCCEEDED") {
          emitSuccessEvent();

          enqueueSnackbar(t("paypalPaymentForm.paymentCompleted"), {
            variant: "success",
          });

          window.location = payment.returnUrl;

          return;
        }

        if (payment.completeError) {
          const errorDetail =
            Array.isArray(payment.completeError.details) &&
            payment.completeError.details[0];

          if (errorDetail) {
            if (errorDetail.issue === "INSTRUMENT_DECLINED") {
              ReactGA.event({
                category: "Product",
                action: "Restart pay",
                label: "paypal",
              });

              return actions.restart();
            } else {
              ReactGA.event({
                category: "Product",
                action: "Pay error",
                label: "paypal",
              });
              setPaymentHasError(true);

              setPaypalErrorDescription(
                `${errorDetail.description} (${payment.completeError.debug_id})`
              );
            }
          }
        }

        setLoading(false);
        setPaypalError(true);

        enqueueSnackbar(t("paypalPaymentForm.completeError"), {
          variant: "error",
          autoHideDuration: 3000,
        });

        return;
      })
      .catch((err) => {
        console.error(err);
        setError(err);
      });
  };

  const handlers = {
    onApprove,
  };

  if (paymentData.subscriptionData) {
    handlers.createSubscription = createSubscription;
  } else if (paymentData.orderData) {
    handlers.createOrder = createOrder;
  }

  const onPaypalError = (err) => {
    setPaymentHasError(true);
    ReactGA.event({
      category: "Product",
      action: "Pay error",
      label: "paypal",
    });
    console.error("PayPal error:", err);

    // Показываем ошибку пользователю
    if (err && err.message) {
      setPaypalErrorDescription(err.message);
      setPaypalError(true);
    }
  };

  const handleCardFieldsReady = useCallback(() => {
    if (!cardFieldsReadyRef.current) {
      cardFieldsReadyRef.current = true;
      if (cardFieldsTimeoutRef.current) {
        clearTimeout(cardFieldsTimeoutRef.current);
        cardFieldsTimeoutRef.current = null;
      }
      setCardFieldsEligible(true);
    }
  }, []);

  const SubmitCardPayment = ({ onReady }) => {
    const { cardFieldsForm } = usePayPalCardFields();

    useEffect(() => {
      if (cardFieldsForm) {
        onReady?.();
      }
    }, [cardFieldsForm, onReady]);

    const submitHandler = () => {
      if (typeof cardFieldsForm?.submit !== "function") {
        console.warn("Card Fields submit function is not available");
        return;
      }

      setLoading(true);
      cardFieldsForm
        .submit()
        .then((order) => {
          console.log("Card Fields: Payment submitted successfully", order);
          // Order creation is handled by onApprove callback
          return order;
        })
        .catch((err) => {
          console.error("Card Fields: Submit error", err);
          setLoading(false);
          onPaypalError(err);
        });
    };

    return (
      <Box sx={{ mt: 3 }}>
        <Button
          onClick={submitHandler}
          variant="contained"
          fullWidth
          size="large"
          disabled={loading || !cardFieldsForm}
        >
          {loading
            ? t("paypalPaymentForm.processing", "Обработка...")
            : t("paypalPaymentForm.payNow", "Оплатить")}
        </Button>
      </Box>
    );
  };

  SubmitCardPayment.propTypes = {
    onReady: PropTypes.func,
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={3}>
        {paypalError && (
          <Grid item xs={12}>
            <Alert severity="error">
              {paypalErrorDescription || t("paypalPaymentForm.paymentError")}
            </Alert>
          </Grid>
        )}

        {descriptionComponent && (
          <Grid item xs={12}>
            {descriptionComponent}
          </Grid>
        )}

        {/* PayPal Account Payment */}
        <Grid item xs={12}>
          <Paper elevation={0}>
            <PayPalButtons
              style={style}
              disabled={loading}
              showSpinner={loading}
              onError={onPaypalError}
              fundingSource="paypal"
              {...handlers}
            />
          </Paper>
        </Grid>

        {/* Divider */}
        <Grid item xs={12}>
          <Divider>
            <Typography variant="body2" color="text.secondary">
              {t("paypalPaymentForm.or", "или")}
            </Typography>
          </Divider>
        </Grid>

        {cardFieldsEligible === false && (
          <Grid item xs={12}>
            <Alert severity="warning">
              {t(
                "paypalPaymentForm.cardUnavailable",
                "Оплата картой недоступна для вашего PayPal аккаунта. Попробуйте другой способ оплаты или свяжитесь с поддержкой."
              )}
            </Alert>
          </Grid>
        )}

        {/* Card Payment Form */}
        {paymentData.orderData && (
          <Grid item xs={12}>
            <Paper elevation={0}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                {t(
                  "paypalPaymentForm.cardDescription",
                  "Введите данные банковской карты"
                )}
              </Typography>
              <PayPalCardFieldsProvider
                style={cardFieldsStyle}
                createOrder={() => {
                  console.log("Card Fields: Creating order");
                  return createOrderForCardFields();
                }}
                onApprove={(data, actions) => {
                  console.log("Card Fields: Payment approved", data);
                  return onApprove(data, actions);
                }}
                onError={(err) => {
                  console.error("Card Fields error:", err);
                  onPaypalError(err);
                }}
                onCancel={() => {
                  setLoading(false);
                  console.log("Card Fields: Payment cancelled");
                }}
              >
                <Box sx={{ mb: 1 }}>
                  <PayPalNumberField />
                </Box>
                <Box sx={{ mb: 1 }}>
                  <PayPalNameField />
                </Box>
                <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <PayPalExpiryField />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <PayPalCVVField />
                  </Box>
                </Box>
                <SubmitCardPayment onReady={handleCardFieldsReady} />
              </PayPalCardFieldsProvider>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

PaypalCheckout.propTypes = {
  paymentHasError: PropTypes.bool,
  setPaymentHasError: PropTypes.func.isRequired,
  descriptionComponent: PropTypes.object,
  paymentId: PropTypes.string.isRequired,
  paymentData: PropTypes.shape({
    amount: PropTypes.shape({
      value: PropTypes.number.isRequired,
      currency: PropTypes.string.isRequired,
    }),
    orderData: PropTypes.object,
    subscriptionData: PropTypes.object,
  }).isRequired,
  setLoading: PropTypes.func,
  loading: PropTypes.bool,
  setError: PropTypes.func,
  returnUrl: PropTypes.string.isRequired,
  enqueueSnackbar: PropTypes.func,
};

export default withSnackbar(PaypalCheckout);
